import type { GetStaticPropsContext, NextPage } from 'next';
import DailySynbioticEmLocale from 'locale/en/daily-synbiotic-em.json';
import { apiPlansGetV2, planPageType } from 'api/plans/get';
import { getYotpoBottomline } from 'lib/utils/get-yotpo-bottomline';
import { getYotpoLlmSchema } from 'lib/utils/get-yotpo-llm-schema';

import { getShopifyVariantResults } from 'lib/shopify/get-shopify-variants-direct';
import { buildFaqSchema } from 'lib/utils/build-faq-schema';
import { attachPdpModulesToProps } from 'components/pdp/modules/get-pdp-modules';
import { richTextToHtml } from 'components/pdp/modules/helpers';
import type {
  PdpModules,
  PdpModulesSource,
} from 'components/pdp/modules/types';
import { mqXXLargeMin, mqLargeMin } from 'styled/tools/media-queries';
import { createGlobalStyle } from 'styled-components';
import { PageTypePdp } from 'components/styles/pdp';
import { YotpoReviewScript } from 'components/pdp/yotpo-reviews-script';
import { Product } from 'entities/product';
import StickyBar from 'components/global/StickyBar';
import { usePdpStickyBar } from 'hooks/use-pdp-sticky-bar';
import { getProductProps } from 'lib/utils/get-product-static-props';
import { activeMiniSale, computeMiniSalePrice } from 'lib/mini-sale/mini-sale';
import { usePdpProductViewed } from 'hooks/use-pdp-product-viewed';
import { InternationalCurrencyModal } from 'components/pdp/international-currency-modal';
import Buybox from 'components/pdp/buybox';
import ProductEssentials from 'components/pdp/product-essentials';
import ProductBenefits from 'components/pdp/product-benefits';
import ProductCompareChart from 'components/pdp/product-compare';
import ProductTesting from 'components/pdp/product-testing';
import ProductHead from './components/ProductHead';
import { ReviewsDisplay } from 'components/pdp/reviews-display';
import { YotpoLlmSchema } from 'components/pdp/yotpo-llm-schema';
import { PRODUCT_IMAGE_DS01 } from 'lib/product-data';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';

import SectionCurious from 'components/pdp/ds01/ds-01-faqs';
import ProductTransformative from 'components/pdp/ds01/product-transformative';
import SustainableDelivery from 'components/pdp/ds01/sustainable-delivery';

const GlobalStyleDailySynbiotic = createGlobalStyle`
  .site-content {
    background-color: var(--fill-primary);

    @media ${mqLargeMin}{
      padding: 0;
    }
    #srp0-sticky {
      z-index: 100;
      transform: translateY(0);
    }
    #add-to-cart-new {
      display: none;
    }
  }
  .optly-hero-text {
    top: 20% !important;
    @media ${mqXXLargeMin} {
      top: 25% !important;
    }
  }

  #add-to-cart-cta:focus {
    background-color: var(--fill-brand) !important;
  }

  ${PageTypePdp}
`;

interface DailySynbioticEmPageProps {
  yotpoAverageScore: string | null;
  yotpoTotalReviews: string | null;
  yotpoLlmSchemaHtml?: string;
  siteBuy: any;
  productInfo: any;
  modules: PdpModules;
  modulesSource: PdpModulesSource;
  products: Product[];
  faqSchema?: object;
  ds01SalePrice?: string | null;
  salePriceValidUntil?: string | null;
}

const PageData = {
  page_type: 'pdp',
  page_class: 'page-daily-synbiotic-em',
  page_name: DailySynbioticEmLocale.page_name,
  page_title: DailySynbioticEmLocale.page_title,
  page_description: DailySynbioticEmLocale.page_desc,
};

const DailySynbioticPage: NextPage<DailySynbioticEmPageProps> = ({
  yotpoAverageScore,
  yotpoTotalReviews,
  yotpoLlmSchemaHtml,
  siteBuy,
  productInfo,
  modules,
  products,
  faqSchema,
  ds01SalePrice,
  salePriceValidUntil,
}) => {
  const { selectedSku, setSelectedSku, product } = usePdpProductViewed({
    products,
    sku: PRODUCT_SKUS.DAILY_SYNBIOTIC,
  });
  const stickyBarProps = usePdpStickyBar(selectedSku);

  const productImages = [];
  productInfo?.media?.nodes?.forEach((mediaItem) => {
    if (mediaItem.mediaContentType === 'IMAGE') {
      productImages.push(mediaItem.previewImage.url);
    }
  });

  return (
    <>
      <GlobalStyleDailySynbiotic />
      <YotpoReviewScript />
      <ProductHead
        productName={productInfo.name}
        productImages={productImages}
        productDescription={productInfo.description}
        productSku={PRODUCT_SKUS.DAILY_SYNBIOTIC}
        productGtin=''
        canonicalUrl='https://seed.com/daily-synbiotic'
        productPrice={productInfo?.priceRange?.minVariantPrice?.amount}
        salePrice={ds01SalePrice}
        priceValidUntil={salePriceValidUntil}
        pageTitle={`Seed • ${PageData.page_title}`}
        ogImageUrl='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1759517363/structured-data/og-ds.png'
        ogImageAlt={PageData.page_title}
        faqSchema={faqSchema}
        yotpoAverageScore={yotpoAverageScore}
        yotpoTotalReviews={yotpoTotalReviews}
      />
      <div className='container ds01-content'>
        <InternationalCurrencyModal location={PageData.page_title} />
        {product && productInfo && (
          <>
            <Buybox
              siteBuy={siteBuy}
              useCartContext
              selectedSku={selectedSku}
              setSelectedSku={setSelectedSku}
              productInfo={productInfo}
              yotpoAverageScore={yotpoAverageScore}
              yotpoTotalReviews={yotpoTotalReviews}
              buyBox={modules.buyBox}
              benefitIcons={modules.transformativeModule?.icons}
              strainsModule={modules.strainsModule}
              ds01SalePrice={ds01SalePrice}
            />

            {modules.essentialsModule && (
              <ProductEssentials
                productInfo={productInfo}
                essentials={modules.essentialsModule}
                selectedSku={selectedSku}
              />
            )}
            {modules.benefitsModule && (
              <ProductBenefits
                benefits={modules.benefitsModule}
                productInfo={productInfo}
                isDS={true}
                clinicalTrials={modules.buyBox?.clinicalTrials}
              />
            )}

            <ProductTransformative
              transformativeModule={modules.transformativeModule}
              strainsModule={modules.strainsModule}
              productInfo={productInfo}
            />
            {modules.compareChartModule && (
              <ProductCompareChart
                productSku={selectedSku}
                compare={modules.compareChartModule}
                productInfo={productInfo}
              />
            )}
            {modules.sustainabilityModule && (
              <SustainableDelivery
                sustainabilityModule={modules.sustainabilityModule}
              />
            )}
            {modules.testingStandardsModule && (
              <ProductTesting
                testingModule={modules.testingStandardsModule}
                productInfo={productInfo}
              />
            )}
            {modules.faqModule && (
              <SectionCurious faqModule={modules.faqModule} />
            )}

            <StickyBar
              {...stickyBarProps}
              imageSrc={PRODUCT_IMAGE_DS01}
              buttonEventName='DS start now'
            />

            <ReviewsDisplay
              imageUrl='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1759517363/structured-data/og-ds.png'
              product={product}
              url='/daily-synbiotic'
            />
            <YotpoLlmSchema html={yotpoLlmSchemaHtml} />
          </>
        )}
      </div>
    </>
  );
};

type DailySynbioticStaticProps = Record<string, unknown> & {
  shopifyProducts?: Record<string, unknown>;
};

export async function getStaticProps({ draftMode }: GetStaticPropsContext) {
  const products = await apiPlansGetV2(planPageType.all);
  const productId = PRODUCT_SKUS.DAILY_SYNBIOTIC;
  const [{ yotpoAverageScore, yotpoTotalReviews }, yotpoLlmSchemaHtml] =
    await Promise.all([
      getYotpoBottomline(productId),
      getYotpoLlmSchema(productId),
    ]);

  const { results } = await getShopifyVariantResults({
    requiredSkus: [productId],
  });

  // Evaluated here rather than via the client `useActiveMiniSale` hook so the
  // sale price is baked into the server-rendered structured data for crawlers.
  const miniSale = activeMiniSale();

  const props: DailySynbioticStaticProps = {
    products,
    yotpoAverageScore,
    yotpoTotalReviews,
    yotpoLlmSchemaHtml,
    ds01SalePrice: computeMiniSalePrice(
      results[productId]?.price?.amount,
      miniSale?.ds01SalePercent,
    ),
    salePriceValidUntil: miniSale?.salePriceValidUntil ?? null,
  };

  props.shopifyProducts = results;

  await getProductProps({
    props: props,
    productSku: productId,
    shopifyVariantData: props.shopifyProducts,
  });

  const modules = await attachPdpModulesToProps('ds-01', props, { draftMode });

  if (modules.faqModule) {
    const faqItems = modules.faqModule.items
      .map((faq) => ({
        question: faq.question ?? '',
        answer: richTextToHtml(faq.answer) ?? '',
      }))
      .filter((item) => item.question);
    props.faqSchema = buildFaqSchema(faqItems);
  }

  return {
    props,
    revalidate: 60,
  };
}

export default DailySynbioticPage;
